import { Component, EventEmitter, Input, OnDestroy, Optional, Output, Self } from "@angular/core";
import { NgControl } from "@angular/forms";
import { NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { Deferred, ListOption } from '../../../../modules/tcm-core.module';
import { from, merge, Observable, of } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import { AutocompleteAddressDetailsViewModel } from "../../../../models/addresses";
import { GeocodingDataService } from "../../../../services/geocoding-data.service";
import { TextInputComponent } from "../../../../modules/tcm-form/controls/text-input/text-input.component";

@Component({
    selector: 'tcm-address-autocomplete',
    templateUrl: './tcm-address-autocomplete.component.html',
    inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
    styleUrls: ['tcm-address-autocomplete.component.scss'],
    standalone: false
})
export class TcmAddressInputComponent extends TextInputComponent implements OnDestroy {

  @Input() override maxLength: number;
  @Input() city: string;
  @Input() searchInAllCountries: boolean;

  @Output() addressSelect = new EventEmitter<AutocompleteAddressDetailsViewModel>();

  private _controlCancellationTokenDeferred: Deferred;
  private _requestCancellationTokenDeferred: Deferred;

  constructor(@Self() @Optional() protected override ngModel: NgControl, private geocodingDataService: GeocodingDataService) {
    super(ngModel)

    this._controlCancellationTokenDeferred = new Deferred();
  }

  ngOnDestroy() {
    this._controlCancellationTokenDeferred.resolve();
  }

  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());

    return merge(debouncedText$).pipe(
      switchMap(term => term.length < 2 ? of([]) : from(this.autocompleteAddress(term)))
    );
  }

  formatResult(value: ListOption<string>): string {
    return value.displayName;
  }

  async onSelectAddress($event: NgbTypeaheadSelectItemEvent) {
    $event.preventDefault();

    const addressModel = await this.getAddressDetails($event.item.value);

    this.addressSelect.emit(addressModel);
    this.value = addressModel.address1;
  }

  private autocompleteAddress(string: any): Promise<ListOption<string>[]> {
    this.resolveProcessingRequest();

    return this.geocodingDataService.getAddressOptionsAsync(string, this.searchInAllCountries, this._requestCancellationTokenDeferred.promise());
  }

  private getAddressDetails(placeId: any): Promise<AutocompleteAddressDetailsViewModel> {
    this.resolveProcessingRequest();

    return this.geocodingDataService.getAddressDetailsAsync(placeId, this._requestCancellationTokenDeferred.promise());
  }

  private resolveProcessingRequest() {
    if (this._requestCancellationTokenDeferred) {
      this._requestCancellationTokenDeferred.resolve();
    }

    this._requestCancellationTokenDeferred = this._controlCancellationTokenDeferred.createDeferred();
  }
}